import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { ActionSettings } from 'components/Advanced';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultAPIResult, TFieldState } from 'types/state.interface';
import { getAllData } from 'utils/fetchAllData';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { EFieldType, type TField, createEmptyField } from '@timeedit/types';
import api from '../services/api.service';

const initialState: TFieldState = {
  loading: false,
  isSaving: {
    main: false,
    compare: false,
  },
  hasErrors: false,
  selectedField: null,
  selectedFieldCompare: null,
  allFields: [],
  errorMessage: '',
  advancedSettings: {},
  fieldMapping: {},
  globalOrder: [],
  orderByType: {},
};

// Slice
const slice = createSlice({
  name: 'fields',
  initialState,
  reducers: {
    fetchFieldsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchFieldsSuccess: (
      state,
      { payload }: PayloadAction<{ results: TField[] }>
    ) => {
      state.allFields = payload.results;
      state.fieldMapping = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchFieldsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fieldsOfTypeLoaded: (
      state,
      {
        payload: { typeId, fields },
      }: PayloadAction<{ typeId: number; fields: TField[] }>
    ) => {
      for (const field of fields) {
        const index = state.allFields.findIndex((f) => f.id === field.id);
        if (index === -1) state.allFields.push(field);
        else state.allFields[index] = field;
        state.fieldMapping[field.id] = field;
      }
      state.orderByType[typeId] = fields.map((field) => field.id);
    },
    createFieldRequest: (state) => {
      state.hasErrors = false;
      state.isSaving.main = true;
    },
    createFieldSuccess: (state, { payload }: PayloadAction<TField>) => {
      state.allFields = state.allFields.filter((field: TField) => !!field.id);
      state.allFields.unshift(payload);
      state.fieldMapping[payload.id] = payload;
      state.selectedField = undefined;
      state.isSaving.main = false;
      state.hasErrors = false;
    },
    createUnsavedFieldSuccess: (state, { payload }: PayloadAction<TField>) => {
      state.allFields = state.allFields.filter((field: TField) => !!field.id);
      state.allFields.unshift(payload);
      state.fieldMapping[payload.id] = payload;
      state.selectedField = payload;
      finishedLoadingSuccess(state);
    },
    createFieldFailure: (state) => {
      state.isSaving.main = false;
      state.hasErrors = true;
    },
    setCurrentField: (state, { payload }: PayloadAction<TField>) => {
      const previousField = state.selectedField;
      state.selectedField = payload;
      state.errorMessage = '';
      if (!payload && previousField?.id === 0) {
        state.allFields = state.allFields.filter((field: TField) => !!field.id);
      }
    },
    setCurrentFieldCompare: (state, { payload }: PayloadAction<TField>) => {
      state.selectedFieldCompare = payload;
    },
    onDiscardForm: (
      state,
      { payload }: PayloadAction<{ isFirstDrawer: boolean; id: number }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.selectedField = state.fieldMapping[id];
      } else {
        state.selectedFieldCompare = state.fieldMapping[id];
      }
    },
    deleteFieldRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteFieldSuccess: (state, payload: PayloadAction<Pick<TField, 'id'>>) => {
      state.allFields = state.allFields.filter(
        (field: TField) => field.id !== payload.payload.id
      );
      state.fieldMapping[payload.payload.id] = undefined;
      finishedLoadingSuccess(state);
    },
    deleteFieldFailure: (state, { payload }: PayloadAction<string>) => {
      state.errorMessage = payload;
      finishedLoadingFailure(state);
    },
    updateFieldRequest: (
      state,
      { payload }: PayloadAction<{ isFirstDrawer: boolean }>
    ) => {
      state.hasErrors = false;
      state.isSaving[payload.isFirstDrawer ? 'main' : 'compare'] = true;
    },
    updateFieldSuccess: (
      state,
      { payload: { isFirstDrawer, field: updatedField } }
    ) => {
      state.errorMessage = '';
      const index = state.allFields.findIndex(
        (field: TField) => field.id === updatedField.id
      );
      state.allFields[index] = updatedField;
      state.fieldMapping[updatedField.id] = updatedField;

      state.isSaving[isFirstDrawer ? 'main' : 'compare'] = false;

      if (isFirstDrawer) {
        state.selectedField = undefined;
      } else {
        state.selectedFieldCompare = undefined;
      }
      state.hasErrors = false;
    },
    updateFieldFailure: (
      state,
      { payload }: PayloadAction<{ message: string; isFirstDrawer: boolean }>
    ) => {
      state.hasErrors = true;
      state.errorMessage = payload.message;
      state.isSaving[payload.isFirstDrawer ? 'main' : 'compare'] = false;
    },
    fetchAdvancedSettingsFieldsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        advancedSettings: { results: ActionSettings[] };
        fieldId: number;
      }>
    ) => {
      const { fieldId, advancedSettings } = payload;
      state.advancedSettings[fieldId] = advancedSettings.results;
    },
    fetchGlobalFieldOrderRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchGlobalFieldOrderSuccess: (
      state,
      { payload }: PayloadAction<{ results: TField[] }>
    ) => {
      state.globalOrder = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchGlobalFieldOrderFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setGlobalFieldOrderReducer: (
      state,
      { payload }: PayloadAction<TField[]>
    ) => {
      state.globalOrder = payload;
    },
    updateGlobalFieldOrderReducer: (
      state,
      { payload }: PayloadAction<TField[]>
    ) => {
      state.globalOrder = payload;
    },
  },
});

export default slice.reducer;

export const globalOrderSelector = (state: TRootState) =>
  state.fields.globalOrder;
export const fieldsLoading = (state: TRootState) => state.fields.loading;
export const selectFieldOrderOfType = (state: TRootState, typeId: number) =>
  state.fields.orderByType[typeId];
export const isSavingSelectedFieldLoading = (state: TRootState) =>
  state.fields.isSaving.main;
export const isSavingSelectedFieldCompareLoading = (state: TRootState) =>
  state.fields.isSaving.compare;
export const fieldsSelector = (state: TRootState) => state.fields.allFields;
export const selectFieldsOfType = (types: string | string[]) =>
  createSelector(
    (state: TRootState) => state.fields.allFields,
    (allFields) => {
      if (typeof types === 'string') {
        return allFields.filter((field) => field.fieldType === types);
      }

      return allFields.filter((field) => types.includes(field.fieldType));
    }
  );
export const isNewFields = (record: any) => {
  return record.id === 0;
};
export const selectedFieldSelector = (state: TRootState) => {
  return state.fields.selectedField;
};
export const selectedFieldCompareSelector = (state: TRootState) => {
  return state.fields.selectedFieldCompare;
};
const EMPTY_ARRAY: ActionSettings[] = [];

export const advancedFieldsSelector =
  (fieldsId: number) => (state: TRootState) =>
    state.fields.advancedSettings[fieldsId] || EMPTY_ARRAY;

export const fieldMappingSelector: (
  state: TRootState
) => Record<number, TField | undefined> = (state: TRootState) =>
  state.fields.fieldMapping;

export const getListIdSelected = (state: TRootState) => {
  const firstDrawer = state.fields.selectedField;
  const secondDrawer = state.fields.selectedFieldCompare;
  const result = [];
  if (firstDrawer) result.push(firstDrawer.id);
  if (secondDrawer) result.push(secondDrawer.id);
  return result;
};

export const {
  fetchFieldsRequest,
  fetchFieldsSuccess,
  fetchAdvancedSettingsFieldsSuccess,
  fetchFieldsFailure,
  fieldsOfTypeLoaded,
  createFieldRequest,
  createFieldSuccess,
  createFieldFailure,
  createUnsavedFieldSuccess,
  setCurrentField,
  setCurrentFieldCompare,
  deleteFieldRequest,
  deleteFieldSuccess,
  deleteFieldFailure,
  updateFieldRequest,
  updateFieldSuccess,
  updateFieldFailure,
  onDiscardForm,
  fetchGlobalFieldOrderRequest,
  fetchGlobalFieldOrderSuccess,
  fetchGlobalFieldOrderFailure,
  setGlobalFieldOrderReducer,
} = slice.actions;

type TFetchFieldsParams = {
  sort?: string;
  fieldType?: string;
  searchText?: string;
  typeId?: string;
};

export const fetchFields = createAppAsyncThunk<void, void | TFetchFieldsParams>(
  'fields/fetchFields',
  async (params, { dispatch }) => {
    const { sort, fieldType, searchText, typeId } = params || {};
    try {
      dispatch(fetchFieldsRequest());
      const params = {
        ...(sort && { sort }),
        ...(fieldType && { fieldType }),
        ...(searchText && { searchText }),
        ...(typeId && { typeId }),
      };
      const fields = await getAllData(
        {
          endpoint: `/fields?${new URLSearchParams(params)}`,
        },
        'get'
      );
      dispatch(fetchFieldsSuccess(fields));
    } catch (e) {
      dispatch(fetchFieldsFailure());
      return console.error(e);
    }
  }
);

export const fetchFieldsGlobalOrder = createAppAsyncThunk<
  void,
  void | TFetchFieldsParams
>('fields/fetchFieldsGlobalOrder', async (params, { dispatch }) => {
  try {
    dispatch(fetchGlobalFieldOrderRequest());
    const params = {
      sort: 'FIELD_ORDER',
    };
    const fields = await getAllData(
      {
        endpoint: `/fields/find`,
        data: params,
        suppressNotification: true,
      },
      'post'
    );
    dispatch(fetchGlobalFieldOrderSuccess(fields));
  } catch (e) {
    dispatch(fetchGlobalFieldOrderFailure());
    return console.error(e);
  }
});

// One request serves both the field list and the global order; pages that need both save
// two full fetches (the field list is ~350 KB and takes seconds)
export const fetchFieldsInGlobalOrder = createAppAsyncThunk<void, void>(
  'fields/fetchFieldsInGlobalOrder',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchFieldsRequest());
      const fields = await getAllData(
        { endpoint: '/fields?sort=FIELD_ORDER' },
        'get'
      );
      dispatch(fetchFieldsSuccess(fields));
      dispatch(fetchGlobalFieldOrderSuccess(fields));
    } catch (e) {
      dispatch(fetchFieldsFailure());
      return console.error(e);
    }
  }
);

export const createField = createAppAsyncThunk<void, TField>(
  'fields/createField',
  async (field, { dispatch }) => {
    try {
      dispatch(createFieldRequest());
      const { id, history, ...fieldBody } = field;
      const response = await api.post({
        endpoint: `/fields`,
        data: fieldBody,
      });
      dispatch(createFieldSuccess(response));
      dispatch(fetchFields());
    } catch (e: any) {
      dispatch(createFieldFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const setGlobalFieldOrder =
  (fields: TField[]): TAppThunk =>
  async (dispatch) => {
    dispatch(setGlobalFieldOrderReducer(fields));
  };

export const createUnsavedField = createAppAsyncThunk(
  'fields/createUnsavedField',
  async (_, { dispatch }) => {
    try {
      const response: TField = {
        ...createEmptyField(),
        fieldType: EFieldType.TEXT,
      };
      dispatch(createUnsavedFieldSuccess(response));
    } catch (e: any) {
      dispatch(createFieldFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const saveStagedGlobalFieldOrder = createAppAsyncThunk<void, TField[]>(
  'fields/saveStagedGlobalFieldOrder',
  async (fields, { dispatch }) => {
    dispatch(fetchFieldsRequest());
    try {
      const newOrder = fields.map((field) => field.id);
      await api.patch({
        endpoint: `/fields/sort-order`,
        data: { fields: newOrder },
      });
      // The endpoint answers with the id list, not with fields: reload the order
      await dispatch(fetchFieldsGlobalOrder());
    } catch (e: any) {
      dispatch(fetchFieldsFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const setSelectedField =
  (field: TField): TAppThunk =>
  async (dispatch) => {
    dispatch(setCurrentField(field));
  };

export const copyField = createAppAsyncThunk<void, TField>(
  'fields/copyField',
  async (field, { dispatch }) => {
    try {
      const response: TField = {
        ...field,
        id: 0,
        description: '',
        documentation: '',
        categories: [],
        referenceFields: [],
        checkSum: 0,
        name: `${field.name}-copy`,
        extId: `${field.extId}-copy`,
        history: [],
      };
      dispatch(createUnsavedFieldSuccess(response));
    } catch (e) {
      dispatch(createFieldFailure());
      return console.error(e);
    }
  }
);

export const deleteField = createAppAsyncThunk<0 | 1, TField>(
  'fields/deleteField',
  async (field, { dispatch }) => {
    try {
      dispatch(deleteFieldRequest());
      await api.delete({
        endpoint: `/fields/${field.id}`,
      });
      dispatch(deleteFieldSuccess(field));
      return 1;
    } catch (e: any) {
      dispatch(deleteFieldFailure(e.response.data.message));
      console.error(e.response.data ? e.response.data : e.response);
      return 0;
    }
  }
);

export const updateField = createAppAsyncThunk<
  void,
  { data: TField; isFirstDrawer: boolean }
>(
  'fields/updateField',
  async ({ data: field, isFirstDrawer }, { dispatch }) => {
    try {
      dispatch(updateFieldRequest({ isFirstDrawer }));
      const { id, history, ...fieldBody } = field;
      const response = (await api.patch({
        endpoint: `/fields/${field.id}`,
        data: fieldBody,
      })) as TField;
      dispatch(updateFieldSuccess({ isFirstDrawer, field: response }));
    } catch (e: any) {
      dispatch(
        updateFieldFailure({ message: e.response.data.message, isFirstDrawer })
      );
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const fetchAdvancedSettingsForField = createAppAsyncThunk<void, number>(
  'fields/fetchAdvancedSettingsForField',
  async (fieldId, { dispatch }) => {
    try {
      if (fieldId) {
        const advancedSettings = await api.get({
          endpoint: `/fields/${fieldId}/advanced`,
        });
        dispatch(
          fetchAdvancedSettingsFieldsSuccess({
            fieldId,
            advancedSettings,
          })
        );
      }
    } catch (e) {
      dispatch(fetchFieldsFailure());
      return console.error(e);
    }
  }
);

export const checkFirstSaveDisabled =
  (): TAppThunk<boolean> => (_, getState) => {
    const state = getState();
    const firstDrawer = state.fields.selectedField;
    const fieldMap = state.fields.fieldMapping;
    return (
      !firstDrawer || checkSavedDisabled(firstDrawer, fieldMap[firstDrawer?.id])
    );
  };

export const checkSecondSaveDisabled =
  (): TAppThunk<boolean> => (_, getState) => {
    const state = getState();
    const secondDrawer = state.fields.selectedFieldCompare;
    const fieldMap = state.fields.fieldMapping;
    return (
      !secondDrawer ||
      checkSavedDisabled(secondDrawer, fieldMap[secondDrawer.id])
    );
  };

export const getFirstDrawer = (): TAppThunk<TField | null> => (_, getState) => {
  return getState().fields.selectedField;
};
